/**
 * ============================================================================
 * DIALOGS STORE - CORE STORE INITIALIZATION
 * ============================================================================
 * 
 * This file initializes the main dialogs store using the GenericStore pattern.
 * The store handles caching and syncing of dialog items for games.
 * 
 * Architecture:
 * - Uses storeRegistry for persistent caching
 * - 30-minute TTL for cached data
 * - Supports optimistic updates for instant UI feedback
 * 
 * @module dialogs/store
 */

import { storeRegistry } from "@/rerun-manager/GenericStore";
import type { Dialog } from "./types";

// ============================================================================
// STORE CONFIGURATION
// ============================================================================

/** Cache time-to-live in milliseconds (30 minutes) */
const CACHE_TTL_MS = 1000 * 60 * 30;

/** Store key used for persistent caching */
const STORE_KEY = 'dialogs';

// ============================================================================
// STORE INSTANCE
// ============================================================================

/**
 * Main store instance for dialogs.
 * 
 * Features:
 * - Persistent caching with configurable TTL
 * - Automatic sync from remote API
 * - Optimistic update support
 * 
 * @example
 * ```ts
 * // Get all cached data
 * const allDialogs = dialogStore.getData();
 * 
 * // Update data
 * dialogStore.setData(newData);
 * 
 * // Sync from remote
 * await dialogStore.syncFromRemote(fetchFn);
 * ```
 */
export const dialogStore = storeRegistry.getStore<Dialog>({
  key: STORE_KEY,
  ttlMs: CACHE_TTL_MS,
});

// ============================================================================
// STORE UTILITIES
// ============================================================================

/**
 * Clear the dialogs cache.
 * Call this when you need to force a fresh fetch from the API.
 */
export async function clearDialogCache(): Promise<void> {
  try {
    await dialogStore.clearCache();
    console.log('[DialogsStore] Cache cleared successfully');
  } catch (error) {
    console.error('[DialogsStore] Failed to clear cache:', error);
  }
}
